import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import PostComposer from './PostComposer';
import PostCard from './PostCard';
import AdSlot from './AdSlot';
import { supabase } from '../lib/supabase';
import * as api from '../lib/api';
import { defaultAvatar } from '../lib/avatarDefaults';

const FEED_PAGE_SIZE = 20;
const FEED_REFRESH_CAP = 100; // matches the server's maximum page size
const FEED_AD_EVERY_N = 5; // fallback cadence; each ad's `every_n` is honored at render time

const Feed = ({ searchQuery, onClearSearch, challengePreFill, onChallengeConsumed, onNavigate }) => {
  const { profile } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchResults, setSearchResults] = useState(null);
  const [newPostsAnnouncement, setNewPostsAnnouncement] = useState('');
  const prevPostCountRef = useRef(0);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadMoreAnnouncement, setLoadMoreAnnouncement] = useState('');
  // Position in the server's ordering where the next page starts, and how many
  // posts are currently loaded (so live refreshes can cover everything on screen).
  const nextOffsetRef = useRef(0);
  const loadedCountRef = useRef(0);

  const isSelfFeed = profile && profile.show_brothers === false && profile.show_sisters === false;

  // Full fetch — shows loading skeleton (initial load only)
  const fetchPosts = useCallback(async () => {
    setLoading(true);
    setSearchResults(null);
    try {
      const params = { paged: true, limit: FEED_PAGE_SIZE };
      if (profile?.id) params.viewer_id = profile.id;
      const res = await api.getPosts(params);
      const loaded = Array.isArray(res.data?.posts) ? res.data.posts : [];
      setPosts(loaded);
      loadedCountRef.current = loaded.length;
      nextOffsetRef.current = res.data?.next_offset || 0;
      setHasMore(!!res.data?.has_more);
    } catch (err) {
      console.error('Error fetching posts:', err);
    } finally {
      setLoading(false);
    }
  }, [profile?.id]);

  // Silent refresh — keeps existing posts visible while updating (after post/comment/like events)
  const silentRefresh = useCallback(async () => {
    try {
      // Refresh the newest posts, covering as many as are already on screen
      // (up to the server cap). Anything loaded further down is kept as is.
      const refreshLimit = Math.min(FEED_REFRESH_CAP, Math.max(FEED_PAGE_SIZE, loadedCountRef.current));
      const params = { paged: true, limit: refreshLimit };
      if (profile?.id) params.viewer_id = profile.id;
      const res = await api.getPosts(params);
      const fresh = Array.isArray(res.data?.posts) ? res.data.posts : [];
      setPosts((prev) => {
        const freshIds = new Set(fresh.map((p) => p.id));
        const tail = prev.slice(refreshLimit).filter((p) => !freshIds.has(p.id));
        if (tail.length === 0) {
          nextOffsetRef.current = res.data?.next_offset || 0;
          setHasMore(!!res.data?.has_more);
        }
        const merged = [...fresh, ...tail];
        loadedCountRef.current = merged.length;
        return merged;
      });
    } catch {}
  }, [profile?.id]);

  const loadMore = async () => {
    if (loadingMore) return;
    setLoadingMore(true);
    try {
      const params = { paged: true, limit: FEED_PAGE_SIZE, offset: nextOffsetRef.current };
      if (profile?.id) params.viewer_id = profile.id;
      const res = await api.getPosts(params);
      const more = Array.isArray(res.data?.posts) ? res.data.posts : [];
      setPosts((prev) => {
        // New posts arriving while scrolling can shift the server's positions, so skip repeats
        const seen = new Set(prev.map((p) => p.id));
        const fresh = more.filter((p) => !seen.has(p.id));
        const merged = [...prev, ...fresh];
        loadedCountRef.current = merged.length;
        // Loading older posts is not "new posts": keep the new-post announcement quiet
        prevPostCountRef.current = merged.length;
        return merged;
      });
      nextOffsetRef.current = res.data?.next_offset || nextOffsetRef.current;
      setHasMore(!!res.data?.has_more && more.length > 0);
      setLoadMoreAnnouncement(
        more.length === 0 ? 'No more posts to load.' : `${more.length} more post${more.length !== 1 ? 's' : ''} loaded.`
      );
      setTimeout(() => setLoadMoreAnnouncement(''), 3000);
    } catch (err) {
      console.error('Error loading more posts:', err);
      setLoadMoreAnnouncement('Could not load more posts. Please try again.');
    } finally {
      setLoadingMore(false);
    }
  };

  // Search function
  const performSearch = useCallback(async (query) => {
    setLoading(true);
    try {
      const res = await api.search(query);
      setSearchResults(res.data);
      setPosts(Array.isArray(res.data?.posts) ? res.data.posts : []);
    } catch (err) {
      console.error('Error searching:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial fetch and subscription setup
  useEffect(() => {
    if (searchQuery) {
      performSearch(searchQuery);
    } else {
      fetchPosts();
    }
  }, [searchQuery, fetchPosts, performSearch]);

  // Real-time subscription for posts
  useEffect(() => {
    const postsSubscription = supabase
      .channel('posts-realtime')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'posts' },
        () => { if (!searchQuery) silentRefresh(); }
      )
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'posts' },
        (payload) => {
          setPosts((prev) =>
            prev.map((post) =>
              post.id === payload.new.id ? { ...post, ...payload.new } : post
            )
          );
        }
      )
      .on(
        'postgres_changes',
        { event: 'DELETE', schema: 'public', table: 'posts' },
        (payload) => {
          setPosts((prev) => prev.filter((post) => post.id !== payload.old.id));
        }
      )
      .subscribe();

    const likesSubscription = supabase
      .channel('likes-realtime')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'likes' }, () => {
        if (!searchQuery) silentRefresh();
      })
      .subscribe();

    const commentsSubscription = supabase
      .channel('comments-realtime')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'comments' }, () => {
        if (!searchQuery) silentRefresh();
      })
      .subscribe();

    return () => {
      supabase.removeChannel(postsSubscription);
      supabase.removeChannel(likesSubscription);
      supabase.removeChannel(commentsSubscription);
    };
  }, [searchQuery, silentRefresh]);

  // Announce new posts to screen readers when the feed updates with new content
  useEffect(() => {
    if (loading) return;
    if (posts.length > prevPostCountRef.current && prevPostCountRef.current > 0) {
      const added = posts.length - prevPostCountRef.current;
      setNewPostsAnnouncement(`${added} new post${added !== 1 ? 's' : ''} added to feed.`);
      const timer = setTimeout(() => setNewPostsAnnouncement(''), 3000);
      prevPostCountRef.current = posts.length;
      return () => clearTimeout(timer);
    }
    prevPostCountRef.current = posts.length;
  }, [posts.length, loading]);

  const handlePostCreated = () => {
    silentRefresh();
  };

  const handlePostDelete = (postId) => {
    setPosts((prev) => prev.filter((p) => p.id !== postId));
  };

  if (loading) {
    return (
      <div className="col-span-12 lg:col-span-6 p-4 lg:p-6" data-testid="feed-loading">
        <div className="animate-pulse space-y-6">
          <div className="h-40 glass-card rounded-arch-xl"></div>
          <div className="h-60 glass-card rounded-arch-xl"></div>
          <div className="h-60 glass-card rounded-arch-xl"></div>
        </div>
      </div>
    );
  }

  return (
    <main className="col-span-12 lg:col-span-6 p-4 lg:p-6" data-testid="main-feed">
      <span className="sr-only" aria-live="polite" aria-atomic="true">{newPostsAnnouncement || loadMoreAnnouncement}</span>
      {/* Self Feed Indicator */}
      {isSelfFeed && !searchQuery && (
        <div className="mb-4 p-4 glass-card rounded-arch-lg border border-secondary/30" data-testid="self-feed-banner">
          <p className="text-sm text-text-main font-medium">Your Personal Feed</p>
          <p className="text-xs text-text-muted mt-1">
            Community visibility is turned off. Posts here are only visible to you. 
            Turn on visibility in Edit Profile to see community posts.
          </p>
        </div>
      )}

      {/* Search Results Header */}
      {searchQuery && (
        <div className="mb-6 p-4 glass-card rounded-arch-lg">
          <div className="flex items-center justify-between mb-2">
            <h2 className="font-heading text-lg font-medium text-primary">
              Search results for "{searchQuery}"
            </h2>
            <button
              onClick={onClearSearch}
              className="flex items-center gap-1 px-3 py-1 text-sm text-text-muted hover:text-primary hover:bg-primary/10 rounded-full transition-colors"
              data-testid="clear-search-feed-btn"
            >
              <span>Clear</span>
              <span className="text-lg">&times;</span>
            </button>
          </div>
          {searchResults?.users?.length > 0 && (
            <div className="mt-3">
              <p className="text-xs font-medium text-text-muted uppercase tracking-wide mb-2">People</p>
              <div className="flex flex-wrap gap-2">
                {searchResults.users.map((user) => (
                  <button
                    key={user.id}
                    onClick={() => onNavigate?.(`user:${user.display_name}`)}
                    className="flex items-center gap-2 px-3 py-2 bg-white/50 dark:bg-white/10 hover:bg-primary/10 rounded-full transition-colors"
                    data-testid={`search-user-${user.id}`}
                  >
                    <img src={user.avatar_url || defaultAvatar(user.gender)} alt={user.display_name} className="w-6 h-6 rounded-full object-cover" />
                    <span className="text-sm text-text-main font-medium">{user.display_name}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
          {searchResults?.posts?.length > 0 && (
            <p className="text-xs font-medium text-text-muted uppercase tracking-wide mt-4 mb-1">
              Posts ({searchResults.posts.length})
            </p>
          )}
        </div>
      )}

      {/* Post Composer */}
      {!searchQuery && <PostComposer onPostCreated={handlePostCreated} isSelfFeed={isSelfFeed} challengePreFill={challengePreFill} onChallengeConsumed={onChallengeConsumed} />}

      {/* Posts List */}
      <div className="space-y-6">
        <AnimatePresence mode="popLayout">
          {(() => {
            const displayPosts = searchQuery
              ? (searchResults?.posts || [])
              : posts;
            if (displayPosts.length === 0) return (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-12 glass-card rounded-arch-xl"
            >
              <p className="text-text-muted">
                {searchQuery
                  ? 'No posts found matching your search.'
                  : isSelfFeed
                  ? 'Your personal feed is empty. Write something for yourself!'
                  : 'No posts yet. Be the first to share!'}
              </p>
            </motion.div>
            );
            return displayPosts.map((post, idx) => (
              <React.Fragment key={post.id}>
                <PostCard
                  post={post}
                  onDelete={handlePostDelete}
                  onNavigate={onNavigate}
                />
                {(idx + 1) % FEED_AD_EVERY_N === 0 && idx !== displayPosts.length - 1 && (
                  <AdSlot placement="feed" key={`ad-feed-${idx}`} />
                )}
              </React.Fragment>
            ));
          })()}
        </AnimatePresence>
      </div>

      {!searchQuery && hasMore && posts.length > 0 && (
        <div className="mt-6 flex justify-center">
          <button
            type="button"
            onClick={loadMore}
            disabled={loadingMore}
            className="min-h-[44px] px-8 py-3 rounded-full glass-card text-sm font-medium text-text-main hover:bg-primary/10 active:scale-[0.98] transition-all disabled:opacity-60"
            data-testid="feed-load-more"
          >
            {loadingMore ? 'Loading...' : 'Load more posts'}
          </button>
        </div>
      )}
    </main>
  );
};

export default Feed;
